import { useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";

import { FullScreenLoader } from "../components/ui/loaders/full-screen-loader.js";
import {
  buildAdminConsentUrl,
  getAdminConsentEndPath,
  getAdminConsentErrorRedirectPath,
  rememberAdminConsentSearch,
} from "../features/auth/admin-consent-logic.js";
import type { AdminConsentFlow } from "../features/auth/admin-consent-logic.js";
import { notifyTeamsAuthFailure } from "../features/auth/teams-auth-popup-callback.js";
import { env } from "../utils/env.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

export const AdminConsentStartPage = ({ flow }: { flow: AdminConsentFlow }) => {
  const location = useLocation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const hasStartedAdminConsentRedirect = useRef(false);

  // Redirect to Microsoft to request admin consent.
  useEffect(() => {
    if (hasStartedAdminConsentRedirect.current) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    rememberAdminConsentSearch(flow, location.search);

    if (!tenantId) {
      hasStartedAdminConsentRedirect.current = true;
      recordErrorInSentry(
        new Error("Tenant ID not found in AdminConsentStartPage")
      );

      notifyTeamsAuthFailure("Tenant ID not found in admin consent start page")
        .then((hasNotifiedTeamsPopup) => {
          if (!hasNotifiedTeamsPopup) {
            window.location.assign(
              getAdminConsentErrorRedirectPath(flow, location.search, "error")
            );
          }
        })
        .catch((error: unknown) => {
          recordErrorInSentry(toError(error));
          window.location.assign(
            getAdminConsentErrorRedirectPath(flow, location.search, "error")
          );
        });
      return;
    }

    hasStartedAdminConsentRedirect.current = true;

    const searchParams = new URLSearchParams(location.search);
    const loginHint = searchParams.get("login_hint") || undefined;

    window.location.assign(
      buildAdminConsentUrl({
        adminConsentEndPath: getAdminConsentEndPath(flow),
        clientId: env.PUBLIC_CLIENT_ID,
        frontendUrl: env.PUBLIC_FRONTEND_URL,
        tenantId,
        ...(loginHint ? { loginHint } : {}),
      })
    );
  }, [flow, location.search, tenantId]);

  return <FullScreenLoader message="Redirecting..." />;
};
